import { useCallback, useEffect, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getPnLMetrics } from '@/api/clarity'
import { formatMetric } from '@/hooks/clarity/util'
import { PnLMetricCardItemModel, PnLMetricCardModel } from '@/types/clarity'
import { dateToString } from '@/utils'

export default function usePnLStackChartMetrics(
  branches: string,
  date: DateRange | undefined
) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [metricCardRecord, setMetricCardRecord] = useState<
    Record<string, PnLMetricCardModel>
  >({})
  const [miniCardData, setMiniCardData] = useState<PnLMetricCardModel>()

  const fetchData = useCallback(async () => {
    const dateArray = generateLast12MonthsDateArray(date)
    const chartRecords: Record<string, any> = {}
    const allRecords: Record<string, PnLMetricCardModel> = {}

    for (const dateItem of dateArray) {
      const { metricCardData } = await fetchPnLMetrics(
        branches,
        dateToString(dateItem, 'from', 'yyyy-mm'),
        dateToString(dateItem, 'to', 'yyyy-mm')
      )

      if (hasAllZeroData(metricCardData)) continue

      const monthKey = dateToString(dateItem, 'from', 'yyyy-mm')

      allRecords[monthKey] = metricCardData

      // Net sales shown as money in chart; use value as amount for bar
      const netSalesForChart: PnLMetricCardItemModel = {
        ...metricCardData.netSales,
        amount: metricCardData.netSales.amount ?? metricCardData.netSales.value,
      }

      chartRecords[monthKey] = {
        netSales: netSalesForChart,
        rentAndOccupancy: metricCardData.rentAndOccupancy,
        aboveUnitAG: metricCardData.aboveUnitAG,
        cogs: metricCardData.cogs,
        storeLabor: metricCardData.storeLabor,
        opEx: metricCardData.opEx,
      }
    }

    setMetricCardRecord(chartRecords)
    setMiniCardData(sumMetricCards(allRecords))
  }, [branches, date])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await fetchData()
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('An unknown error occurred')
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData])

  const refetch = useCallback(() => fetchAllData(), [fetchAllData])

  useEffect(() => {
    if (branches && date) {
      fetchAllData()
    } else {
      setLoading(true)
    }
  }, [fetchAllData, branches, date])

  return { metricCardRecord, miniCardData, loading, error, refetch }
}

async function fetchPnLMetrics(
  branches: string,
  startDate: string,
  endDate: string
) {
  const response = await getPnLMetrics(branches, startDate, endDate)
  const { metrics } = response

  const metricCardData: PnLMetricCardModel = {
    storeProfits: createMetric(metrics?.store_profits, 'percentage'),
    netSales: createMetric(metrics?.net_sales, 'money'),
    rentAndOccupancy: createMetric(metrics?.rent_occupancy, 'percentage'),
    aboveUnitAG: createMetric(metrics?.above_unit_ga, 'percentage'),
    cogs: createMetric(metrics?.cogs, 'percentage'),
    storeLabor: createMetric(metrics?.store_labor, 'percentage'),
    opEx: createMetric(metrics?.op_ex, 'percentage'),
  }

  return { metricCardData }
}

function createMetric(
  data: any,
  metricType: 'percentage' | 'money' | 'number' | 'hour'
): PnLMetricCardItemModel {
  return {
    value: formatMetric(data?.value, 2) || 0,
    mtd: formatMetric(data?.mtd, 2) || 0,
    ytd: formatMetric(data?.ytd, 2) || 0,
    metricType,
    amount: data?.amount ? formatMetric(data.amount, 2) : undefined,
  }
}

function sumMetricCards(
  cards: Record<string, PnLMetricCardModel>
): PnLMetricCardModel {
  const cardValues = Object.values(cards)

  const emptyMetrics = (): PnLMetricCardModel => ({
    storeProfits: { value: 0, mtd: 0, ytd: 0, metricType: 'percentage' },
    netSales: { value: 0, mtd: 0, ytd: 0, metricType: 'money' },
    rentAndOccupancy: {
      value: 0,
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
      amount: 0,
    },
    aboveUnitAG: {
      value: 0,
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
      amount: 0,
    },
    cogs: { value: 0, mtd: 0, ytd: 0, metricType: 'percentage', amount: 0 },
    storeLabor: {
      value: 0,
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
      amount: 0,
    },
    opEx: { value: 0, mtd: 0, ytd: 0, metricType: 'percentage', amount: 0 },
  })

  if (cardValues.length === 0) return emptyMetrics()

  const result = emptyMetrics()
  const metricKeys = Object.keys(result) as (keyof PnLMetricCardModel)[]

  metricKeys.forEach((key) => {
    const metric = cardValues[0][key]
    const isPercentage = metric.metricType === 'percentage'
    const divisor = isPercentage ? cardValues.length : 1

    const sums = cardValues.reduce(
      (acc, card) => {
        const current = card[key]
        return {
          value: acc.value + current.value,
          mtd: acc.mtd + current.mtd,
          ytd: acc.ytd + current.ytd,
          amount: acc.amount + (current.amount || 0),
        }
      },
      { value: 0, mtd: 0, ytd: 0, amount: 0 }
    )

    result[key] = {
      value: sums.value / divisor,
      mtd: sums.mtd / divisor,
      ytd: sums.ytd / divisor,
      metricType: metric.metricType,
      amount: metric.amount !== undefined ? sums.amount / divisor : undefined,
    }
  })

  return result
}

function hasAllZeroData(metricCard: PnLMetricCardModel): boolean {
  return Object.values(metricCard).every((metric) => {
    if (!metric) return true

    const mainValuesZero = !metric.value && !metric.mtd && !metric.ytd
    const amountZero =
      metric.metricType === 'percentage' ? !metric.amount : true

    return mainValuesZero && amountZero
  })
}

function generateLast12MonthsDateArray(
  date: DateRange | undefined
): DateRange[] {
  const endDate = date?.to ? new Date(date.to) : new Date()
  return generateMonthsArray(endDate.getFullYear(), endDate.getMonth())
}

function generateMonthsArray(endYear: number, endMonth: number): DateRange[] {
  const dateArray: DateRange[] = []

  for (let i = 11; i >= 0; i--) {
    const targetMonth = endMonth - i
    const targetYear = endYear + Math.floor(targetMonth / 12)
    const normalizedMonth = ((targetMonth % 12) + 12) % 12

    dateArray.push({
      from: new Date(targetYear, normalizedMonth, 1),
      to: new Date(targetYear, normalizedMonth + 1, 0),
    })
  }

  return dateArray
}
